import { ChangeEvent, InvalidEvent } from "react";
import { Col, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";
import useValueList from "../../../hook/useValueList";
import { ContainerLayoutProps } from "../../../types/common";
import { FormErrorAction, FormUpdateAction } from "../../common/data-entry-reducer";
import NumberInput from "../../common/number-input";
import { useAddressDispatch, useAddressState } from "./address-manager-context";
import './style.scss';

const AdditionalInfoPanel = (props: ContainerLayoutProps) => {
  const { wide } = props;

  const colWidth = wide ? 6 : 12;

  const [deliveryTypes] = useValueList('DELIVERY_TYPE_CODE');
  const [installationTypes] = useValueList('INSTALLTION_TYPE_CODE');
  const [ruralServices] = useValueList('ROUTE_SERVICE_TYPE_CODE');

  const { selectedDataId, form, errors } = useAddressState();

  const dispatch = useAddressDispatch();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    dispatch(new FormUpdateAction(event));

  const handleInvalid = (event: InvalidEvent<HTMLInputElement>) => {
    event.preventDefault();
    dispatch(new FormErrorAction(event));
  };

  return (
    <section>
      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="delivery_type" sm={4}>Delivery Type:</Label>
            <Col sm={8}>
              <Input type="select" id="delivery_type" name="deliveryTypeCodeId"
                value={form?.deliveryTypeCodeId ?? 0} onChange={handleChange}>
                <option value="0">Select a delivery type</option>
                {deliveryTypes?.filter(item => item.lang == null || item.lang !=='FR').map(item =>
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                )}
              </Input>
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="po_number" sm={4}>PO Box/Bag Number:</Label>
            <Col sm={8}>
              <Input id="po_number" name="poBoxNumber"
                placeholder="Enter a PO Box or bag number" maxLength={100}
                value={form?.poBoxNumber ?? ''} onChange={handleChange} />
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="installation_type" sm={4}>Installation Type:</Label>
            <Col sm={8}>
              <Input type="select" id="installation_type" name="installationTypeCodeId"
                value={form?.installationTypeCodeId ?? 0} onChange={handleChange}>
                <option value="0">Select an installation type</option>
                {installationTypes?.filter(item => item.lang == null || item.lang !=='FR').map(item =>
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                )}
              </Input>
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="installation_name" sm={4}>Installation Name:</Label>
            <Col sm={8}>
              <Input id="installation_name" name="installationName"
                placeholder="Enter an installation name"
                value={form?.installationName ?? ''} onChange={handleChange} />
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="rural_type" sm={4}>Rural Service:</Label>
            <Col sm={8}>
              <Input type="select" id="rural_type" name="routeServiceTypeCodeId"
                value={form?.routeServiceTypeCodeId ?? 0} onChange={handleChange}>
                <option value="0">Select a rural service</option>
                {ruralServices?.filter(item => item.lang == null || item.lang !=='FR').map(item =>
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                )}
              </Input>
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="rural_number" sm={4}>Rural Service Number:</Label>
            <Col sm={8}>
              <NumberInput id="rural_number" name="routeServiceNumber"
                aria-label="Rural Service Number"
                placeholder="Enter a rural service number"
                value={form?.routeServiceNumber ?? ''} onChange={handleChange}
                invalid={!!errors.routeServiceNumber} onInvalid={handleInvalid} />
              <FormFeedback>{errors.routeServiceNumber}</FormFeedback>
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="site_number" sm={4}>Site Number:</Label>
            <Col sm={8}>
              <Input id="site_number" name="siteNumber"
                placeholder="Enter a site number" maxLength={100}
                value={form?.siteNumber ?? ''} onChange={handleChange} />
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="compartment_number" sm={4}>Compartment Number:</Label>
            <Col sm={8}>
              <NumberInput id="compartment_number" name="compartmentNumber"
                aria-label="Compartment Number"
                placeholder="Enter a compartment number"
                value={form?.compartmentNumber ?? ''} onChange={handleChange}
                invalid={!!errors.compartmentNumber} onInvalid={handleInvalid} />
              <FormFeedback>{errors.compartmentNumber}</FormFeedback>
            </Col>
          </FormGroup>
        </Col>
      </Row>

      {!!selectedDataId && <Row>
        <Col>
          <FormGroup check>
            <Input type="checkbox" id="address_expired" name="expired"
              checked={!!form?.expired} onChange={handleChange} />
            {' '}
            <Label for="address_expired" check>Expired</Label>
          </FormGroup>
        </Col>
      </Row>
      }
    </section>
  );
};

export default AdditionalInfoPanel;
